import React, { memo, useEffect, useState, Suspense, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import URLSearchParams from "url-search-params";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { ConfigProvider, message, Modal } from "antd";
import { IntlProvider } from "react-intl";
import { Helmet } from "react-helmet";
import Service from "../../service";
import { LoadingOutlined } from "@ant-design/icons";
import AppLocale from "lngProvider";
import MainApp from "./MainApp";
import SignIn from "../SignIn";
import ForgotPassword from "../ForgotPassword";
import ResetPassword from "../ResetPassword";
import UploadDocsCandidate from "../../components/Onboarding/UploadDocsCandidate";
import { setInitUrl, userSignInSuccess } from "appRedux/actions/Auth";
import { setDateFormat, setCurrency, setTimeCalculationMode, setAutoShiftEnabled } from "../../appRedux/actions/Common";
import { saveAtsApproverFlag } from "../../util/atsApproverAccess";
import {
  onLayoutTypeChange,
  onNavStyleChange,
  setThemeType,
} from "appRedux/actions/Setting";
import CircularProgress from "../../components/CircularProgress";

import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_DARK,
} from "../../constants/ThemeSetting";
import FeedbackForm from "../../components/Feedback/FeedbackForm";
import DayOneFeedbackForm from "../../components/Feedback/DayOneFeedbackForm";
import SignedDocCandidate from "../../components/Onboarding/SignedDocCandidate";
import { userSignOut } from "../../appRedux/actions";
import {
  getSharedSso,
  isLogoutPending,
  getSsoEmail,
  startSsoLogoutWatch,
  isPublicCandidateRoute,
  SSO_SWITCH_NOTICE_KEY,
} from "../../utils/ssoCookie";
import DayFiveFeedbackForm from "../../components/Feedback/DayFiveFeedbackForm";
import OutlookConnectedPage from "../../components/OutlookConnectedPage/OutlookConnectedPage";
import CareerPageJobList from "../../components/PublicCareerPage/CareerPageJobList";
import CareerPageJobDetail from "../../components/PublicCareerPage/CareerPageJobDetail";
import CareerPageApply from "../../components/PublicCareerPage/CareerPageApply";
import PublicQuizPage from "../../components/ATS/Screening/PublicQuizPage";
import { APP_TITLE } from "../../constants/permission_id";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

const API_URL = `${process.env.REACT_APP_API_URL}/uploads/company_image` || "http://localhost:8888/uploads/company_image";

const RestrictedRoute = ({
  component: Component,
  location,
  authUser,
  ...rest
}) => {
  const companySlug = localStorage.getItem("companyDomain");
  const history = useHistory();
  const dispatch = useDispatch();
  const [isUserTokenVerified, setIsUserTokenVerified] = useState(false);
  const hasShownPermissionUpdateModalRef = useRef(false);
  const accessToken = localStorage.getItem("accessToken") || null;
  const performForcedSignOut = () => {
    localStorage.removeItem("user_data");
    localStorage.removeItem("is_reporting_manager");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("resourceAccess");
    localStorage.removeItem("is_assistant_reporting_manager");
    localStorage.removeItem("ssoToken");
    localStorage.removeItem("companyDomain");
    localStorage.removeItem("sidebarModuleAccess");
    localStorage.removeItem("permissionsUpdatedAt");
    dispatch(userSignOut());
    history.push("/signin");
  };
  useEffect(() => {
    favicon(authUser);
  }, []);
  useEffect(() => {
    getUserInfo();
  }, [history?.location?.pathname]);

  // const timeoutRef = useRef(null);
  // const LOGOUT_TIMEOUT = 30 * 60 * 1000;

  // useEffect(() => {
  //   const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  //   function resetTimer() {
  //     if (timeoutRef.current) {
  //       clearTimeout(timeoutRef.current);
  //     }
  //     timeoutRef.current = setTimeout(() => {
  //       dispatch(userSignOut())
  //       window.location.href = `${companySlug ? `/${companySlug}/signin` : "/signin"}`;
  //     }, LOGOUT_TIMEOUT);
  //   }

  //   resetTimer();
  //   events.forEach(event => document.addEventListener(event, resetTimer));

  //   return () => {
  //     if (timeoutRef.current) {
  //       clearTimeout(timeoutRef.current);
  //     }
  //     events.forEach(event => document.removeEventListener(event, resetTimer));
  //   };
  // }, []);


  const getUserInfo = async () => {
    try {
      // Public candidate pages (careers, quiz, upload-docs...) are meant for
      // visitors with no session - never bounce them to the staff sign-in.
      if (isPublicCandidateRoute(window.location.pathname)) return;
      if (!authUser || !authUser._id) {
        // history.push(companySlug ? `/${companySlug}/signin` : "/signin");
        history.push("/signin");
        return;
      }
      const params = `/${authUser._id}`;
      let response;
      if (authUser.emp_code) {
        response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: Service.empById + params,
        });
      } else {
        response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: Service.userById + params,
        });
      }
      if (!response) {
        return Service.messageError(Service.error_message);
      }
      if (response?.data && response?.data?.data) {
        const userData = response?.data?.data;
        const newPermissionsUpdatedAt = response?.data?.permissionsUpdatedAt;
        const reportingmanager = response?.data?.is_reporting_manager;
        const assistantreportingmanager =
          response.data.is_assistant_reporting_manager;
        localStorage.setItem("user_data", JSON.stringify(userData));
        localStorage.setItem(
          "is_reporting_manager",
          JSON.stringify(reportingmanager)
        );
        localStorage.setItem(
          "is_assistant_reporting_manager",
          JSON.stringify(assistantreportingmanager)
        );
        // Only overwrite when the server sent a real list - writing
        // JSON.stringify(undefined) stores the string "undefined", which
        // breaks every JSON.parse of resourceAccess (blank screens).
        if (Array.isArray(response?.data?.permissions)) {
          localStorage.setItem(
            "resourceAccess",
            JSON.stringify(response.data.permissions)
          );
        }
        localStorage.setItem(
          "sidebarModuleAccess",
          JSON.stringify(
            Array.isArray(response?.data?.sidebarModules)
              ? response.data.sidebarModules
              : []
          )
        );

        saveAtsApproverFlag(response?.data?.atsApprover);

        // Permissions/plan modules changed since last sync: resourceAccess and
        // sidebarModuleAccess were just refreshed above, so simply adopt the
        // new timestamp and keep the session alive (no forced logout).
        if (newPermissionsUpdatedAt) {
          localStorage.setItem("permissionsUpdatedAt", String(newPermissionsUpdatedAt));
        }

        localStorage.setItem(
          "enableAtsFullFlow",
          String(Boolean(response?.data?.data?.companyId?.enableAtsFullFlow))
        );

        if (response?.data?.dateFormat) {
          dispatch(setDateFormat(response.data.dateFormat));
        }
        if (response?.data?.currency) {
          dispatch(setCurrency(response.data.currency));
        }
        if (response?.data?.timeCalculationMode) {
          dispatch(setTimeCalculationMode(response.data.timeCalculationMode));
        }
        dispatch(setAutoShiftEnabled(response?.data?.autoShiftEnabled === true));

        dispatch(userSignInSuccess(response?.data?.data));
        setIsUserTokenVerified(true);
      } else {
        localStorage.removeItem('user_data')
        localStorage.removeItem('is_reporting_manager')
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('resourceAccess')
        localStorage.removeItem('is_assistant_reporting_manager')
        localStorage.removeItem('ssoToken')
        localStorage.removeItem('companyDomain')
        localStorage.removeItem('enableAtsFullFlow')

        // localStorage.clear();
        // history.push(companySlug ? `/${companySlug}/signin` : "/signin");
        history.push("/signin");
        dispatch(userSignOut());
        Service.messageError(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const favicon = async (userData) => {
    const id = userData?._id;
    if (id) {
      try {
        const params = `/${id}`;
        const response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: Service.getAdminSettings + params,
        });
        const org_favicon =
          response?.data?.data?.config?.adminSetting?.fav_icon;
        changeFavIcon();
      } catch (e) {
        console.log(e);
      }
    }
  };

  const changeFavIcon = () => {
    const favicon = document.getElementById("fav_icon");
    if (favicon) {
      const FavIcon = localStorage.getItem(`FavIcon_${companySlug}`);
      if (FavIcon) {
        favicon.href = FavIcon.startsWith("http") ? FavIcon : `${API_URL}/${FavIcon}`;
      } else {
        // Default HRMS logo when no favicon is found
        favicon.href = "/HrmsLogo2.svg";
      }
    }
  };
  console.log((!isUserTokenVerified && authUser), isUserTokenVerified, '!isUserTokenVerified && authUser', authUser);


  // if (!isUserTokenVerified && authUser) {
  //   return 'null';
  // }
  // if (!isUserTokenVerified || !authUser) {
  //   return <div>Loading...</div>; // or Redirect/Login
  // }

  return (
    <Route
      {...rest}
      render={(props) =>
        authUser && accessToken ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: companySlug ? `/${companySlug}/signin` : "/signin",
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

const AuthRoute = ({ component: Component, location, authUser, ...rest }) => {
  const companySlug = localStorage.getItem("companyDomain");
  const queryParams = new URLSearchParams(location.search);
  const isSuperAdmin = JSON.parse(localStorage.getItem("user_data"));
  const token = localStorage?.getItem('accessToken')
  console.log("🚀 ~ AuthRoute ~ token:", token, authUser, (!token && !authUser))
  const redirect_uri = queryParams.get("redirect_uri");
  const encodedUrl = encodeURIComponent(redirect_uri);
  if (redirect_uri) {
    return (
      <Route
        {...rest}
        render={(props) =>
          (!token && !authUser) ? (
            <Component {...props} />
          ) : (
            <Redirect
              to={{
                pathname: `/${companySlug}/dashboard`,
                search: `?redirect_uri=${encodedUrl}`,
              }}
            />
          )
        }
      />
    );
  } else {
    return (
      <Route
        {...rest}
        render={(props) =>
          (!token && !authUser) ? (
            <Component {...props} />
          ) : (
            <Redirect
              to={{
                pathname: `/${companySlug}/dashboard`,
              }}
            />
          )
        }
      />
    );
  }
};

const App = (props) => {
  const dispatch = useDispatch();
  const { locale, themeType, navStyle, layoutType, themeColor } = useSelector(
    ({ settings }) => settings
  );
  const { loader, alertMessage, showMessage, authUser, initURL } = useSelector(
    ({ auth }) => auth
  );
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();
  const { companyslug } = useParams();
  const favicon = localStorage.getItem(`FaviconUrl_${companyslug}`);
  const faviconUrl = `${API_URL}/${favicon}`;
  let companyDomain = localStorage.getItem("companyDomain");


  useEffect(() => {
    let link = document.createElement("link");
    link.type = "text/css";
    link.rel = "stylesheet";
    link.href = `/css/${themeColor}.css`;

    link.className = "gx-style";
    document.body.appendChild(link);
  }, []);

  // Cross-product user switch: the SignIn cookie-bootstrap only auto-logs-in
  // when there is NO local session, and the route guard bounces an incoming
  // ?token= to the dashboard when already authenticated - so a stale session
  // for user X is never replaced when user Y logs into another WeekMate app.
  // Detect that here (this component is always mounted) by comparing the shared
  // cookie's identity (email) against our local session's. On a mismatch, clear
  // the stale session and hard-reload: the SignIn bootstrap then logs in as the
  // cookie's user. Same-user cookie (or an undecodable token) → no-op.
  useEffect(() => {
    if (isPublicCandidateRoute(window.location.pathname)) return;
    if (isLogoutPending()) return;
    const localToken = localStorage.getItem("accessToken");
    const sharedToken = getSharedSso();
    if (!localToken || !sharedToken) return;
    const cookieEmail = getSsoEmail(sharedToken);
    let localEmail = getSsoEmail(localStorage.getItem("ssoToken"));
    if (!localEmail) {
      try {
        const u = JSON.parse(localStorage.getItem("user_data"));
        const e = u?.email || u?.user?.email || u?.emailId;
        localEmail = e ? String(e).toLowerCase() : null;
      } catch (e) {
        /* ignore */
      }
    }
    if (cookieEmail && localEmail && cookieEmail !== localEmail) {
      // BUG-05 (docs/swap-shift/BUG-SHEET-2026-09-09.md) — this used to clear
      // and reload with zero indication anything happened: a user signed
      // into two WeekMate accounts in one browser (or a consultant/admin
      // with a test account) would silently lose whichever session they
      // opened first, unsaved form state included, with no way to tell
      // "I got logged out" from "I got switched to someone else". The sync
      // itself is correct SSO behaviour (one active identity per browser,
      // by design — see this file's header) and still has to happen
      // unconditionally; what was missing was ever telling the user. A
      // toast can't survive the reload itself, so the notice is stashed in
      // sessionStorage (untouched by the `localStorage.clear()` below) and
      // shown once the page comes back — see the effect below this one.
      try {
        sessionStorage.setItem(SSO_SWITCH_NOTICE_KEY, cookieEmail);
      } catch (e) {
        /* sessionStorage unavailable — proceed without the notice rather than block the sync */
      }
      localStorage.clear(); // keep the shared cookie; clear only our session
      window.location.reload();
    }
  }, []);

  // Other half of the BUG-05 fix above — shows the stashed notice once, on
  // the reloaded page, then clears the flag so it never repeats on a normal
  // refresh.
  useEffect(() => {
    let switchedToEmail;
    try {
      switchedToEmail = sessionStorage.getItem(SSO_SWITCH_NOTICE_KEY);
      if (switchedToEmail) sessionStorage.removeItem(SSO_SWITCH_NOTICE_KEY);
    } catch (e) {
      return;
    }
    if (switchedToEmail) {
      message.warning(
        `You were signed out here because this browser is now signed in as ${switchedToEmail} elsewhere.`,
        6
      );
    }
  }, []);

  // Single logout: if a sibling WeekMate app cleared the shared cookie, tear
  // down this session too. Runs on mount, on an interval, and on visibility/
  // focus so it fires even when this tab never loses/regains focus.
  useEffect(() => {
    if (isPublicCandidateRoute(window.location.pathname)) return undefined;
    return startSsoLogoutWatch({
      hasLocalSession: () => !!localStorage.getItem("accessToken"),
      onLogout: () => {
        dispatch(userSignOut());
        history.push("/signin");
      },
    });
  }, [dispatch, history]);

  useEffect(() => {
    if (initURL === "") {
      dispatch(setInitUrl(location.pathname));
    }
    const params = new URLSearchParams(location.search);

    if (params.has("theme")) {
      dispatch(setThemeType(params.get("theme")));
    }
    if (params.has("nav-style")) {
      dispatch(onNavStyleChange(params.get("nav-style")));
    }
    if (params.has("layout-type")) {
      dispatch(onLayoutTypeChange(params.get("layout-type")));
    }
    setLayoutType(layoutType);
    setNavStyle(navStyle);
  });

  const setLayoutType = (layoutType) => {
    if (layoutType === LAYOUT_TYPE_FULL) {
      document.body.classList.remove("boxed-layout");
      document.body.classList.remove("framed-layout");
      document.body.classList.add("full-layout");
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove("full-layout");
      document.body.classList.remove("framed-layout");
      document.body.classList.add("boxed-layout");
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove("boxed-layout");
      document.body.classList.remove("full-layout");
      document.body.classList.add("framed-layout");
    }
  };

  const setNavStyle = (navStyle) => {
    if (
      navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
      navStyle === NAV_STYLE_DARK_HORIZONTAL ||
      navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
      navStyle === NAV_STYLE_ABOVE_HEADER ||
      navStyle === NAV_STYLE_BELOW_HEADER
    ) {
      document.body.classList.add("full-scroll");
      document.body.classList.add("horizontal-layout");
    } else {
      document.body.classList.remove("full-scroll");
      document.body.classList.remove("horizontal-layout");
    }
  };

  useEffect(() => {
    const companySlug = localStorage.getItem("companyDomain");
    if (location.pathname === "/") {
      if (
        initURL === "" ||
        initURL === "/" ||
        initURL === (companySlug ? `/${companySlug}/signin` : "/signin")
      ) {
        history.push(`/${companySlug}/dashboard`);
      }
      else if (authUser === null) {
        history.push(`/signin`);
      }
      else {
        history.push(initURL);
      }
    }
  }, [authUser, initURL, location, history]);

  useEffect(() => {
    if (themeType === THEME_TYPE_DARK) {
      document.body.classList.add("dark-theme");
      document.body.classList.add("dark-theme");
      let link = document.createElement("link");
      link.type = "text/css";
      link.rel = "stylesheet";
      link.href = "/css/dark_theme.css";
      link.className = "style_dark_theme";
      document.body.appendChild(link);
    }
  }, []);

  useEffect(() => {
    setSystemImages();
  }, []);

  const setSystemImages = async () => {
    try {
      const companySlug = companyslug || localStorage.getItem("companyDomain");
      const FavIcon = localStorage.getItem(`FavIcon_${companySlug}`);
      const favicon = document.getElementById("fav_icon");
      if (favicon) {
        if (FavIcon) {
          favicon.href = FavIcon.startsWith("http") ? FavIcon : `${API_URL}/${FavIcon}`;
        } else {
          // Default HRMS logo when no favicon is found
          favicon.href = "/HrmsLogo2.svg";
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    const companySlug = companyslug || localStorage.getItem("companyDomain");

    const handleStorageChange = (e) => {
      if (e.key === `FavIcon_${companySlug}` || e.key === null) {
        const FavIcon = localStorage.getItem(`FavIcon_${companySlug}`);
        const favicon = document.getElementById("fav_icon");
        if (favicon) {
          if (FavIcon) {
            favicon.href = FavIcon.startsWith("http") ? FavIcon : `${API_URL}/${FavIcon}`;
          } else {
            // Default HRMS logo when no favicon is found
            favicon.href = "/assets/images/AppLogo/Ehrms.svg";
          }
        }
      }
    };

    const handleCustomStorageChange = (e) => {
      if (e.detail.key === `FavIcon_${companySlug}`) {
        const FavIcon = e.detail.newValue;
        const favicon = document.getElementById("fav_icon");
        if (favicon) {
          if (FavIcon) {
            favicon.href = FavIcon.startsWith("http") ? FavIcon : `${API_URL}/${FavIcon}`;
          } else {
            // Default HRMS logo when no favicon is found
            favicon.href = "/assets/images/AppLogo/Ehrms.svg";
          }
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageChange", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageChange", handleCustomStorageChange);
    };
  }, [companyslug]);

  useEffect(() => {
    const handleScroll = (event) => {
      // Capture-phase scroll also fires for page-level scrolls, where
      // event.target is `document`/`window` - neither has .closest(), so guard
      // for a real Element before calling it. A page scroll counts as "not
      // inside a dropdown", so open dropdowns still get closed below.
      const target = event.target;
      const canClosest = target && typeof target.closest === "function";
      // Check if the scroll target is inside an Ant Design dropdown or picker
      const isScrollInsideDropdown =
        canClosest &&
        (target.closest(".ant-select-dropdown") ||
          target.closest(".ant-picker-dropdown") ||
          target.closest(".ant-cascader-menus") ||
          target.closest(".ant-dropdown"));

      if (!isScrollInsideDropdown) {
        // Find if there's an active Ant Design trigger that should be closed
        const activeElement = document.activeElement;
        const isAntdTrigger =
          activeElement?.closest(".ant-select") ||
          activeElement?.closest(".ant-picker") ||
          activeElement?.closest(".ant-cascader-picker");

        if (isAntdTrigger) {
          activeElement.blur();
        }
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const currentAppLocale = AppLocale[locale.locale];

  return (
    <>
      <Helmet>
        <title>{APP_TITLE}</title>
      </Helmet>
      <ConfigProvider locale={currentAppLocale.antd}
      >
        <IntlProvider
          locale={currentAppLocale.locale}
          messages={currentAppLocale.messages}
        >
          <Suspense
            fallback={
              <div className="gx-loader-view">
                <CircularProgress />
              </div>
            }
          >
            {loader && (
              <div className="loader mainloader">
                <LoadingOutlined
                  style={{ fontSize: "50px", color: "#038fde" }}
                />
              </div>
            )}
            {showMessage ? message.error(alertMessage.toString()) : null}
            <Switch>
              <AuthRoute
                path={`${match.url}signin`}
                authUser={authUser}
                location={location}
                component={SignIn}
              />
              {/* <AuthRoute
                exact
                path={`${match.url}:companyslug/signin`}
                authUser={authUser}
                location={location}
                component={SignIn}
              />
              <AuthRoute
                exact
                path={`${match.url}:companyslug/signIn/:token`}
                component={SignIn}
                authUser={authUser}
                location={location}
              /> */}
              <AuthRoute
                path={`${match.url}:companyslug/forgot-password`}
                component={ForgotPassword}
              />
              <AuthRoute
                path={`${match.url}forgot-password`}
                component={ForgotPassword}
              />
              <AuthRoute
                path={`${match.url}:companyslug/signDoc`}
                component={SignedDocCandidate}
              />
              <AuthRoute
                path={`/reset-password/:token`}
                component={ResetPassword}
              />
              <AuthRoute
                exact
                path="/:companyslug/upload-docs"
                component={UploadDocsCandidate}
              />
              <AuthRoute
                path={`${match.url}:companyslug/feedback/:id`}
                component={FeedbackForm}
              />
              <AuthRoute
                path={`${match.url}:companyslug/day-one-feedback`}
                component={DayOneFeedbackForm}
              />
              <AuthRoute
                path={`${match.url}:companyslug/day-five-feedback`}
                component={DayFiveFeedbackForm}
              />
              {/* Public route - Microsoft 365 OAuth callback landing page (no auth required) */}
              <Route
                exact
                path="/:companyslug/settings"
                component={OutlookConnectedPage}
              />
              {/* Public routes — anonymous, unauthenticated career page (no auth required,
                  no login/redirect concern for a logged-in HR user browsing careers) */}
              <Route
                exact
                path="/:companyslug/careers"
                component={CareerPageJobList}
              />
              <Route
                exact
                path="/:companyslug/careers/:publicSlug"
                component={CareerPageJobDetail}
              />
              <Route
                exact
                path="/:companyslug/careers/:publicSlug/apply"
                component={CareerPageApply}
              />
              {/* Public route — magic-link screening quiz, no auth required, the
                  :token itself is the credential (see ATS_ONBOARDING_FLOW_PLAN.md). */}
              <Route
                exact
                path="/quiz/:token"
                component={PublicQuizPage}
              />
              <RestrictedRoute
                path={`${match.url}`}
                authUser={authUser}
                location={location}
                component={MainApp}
              />
            </Switch>
          </Suspense>
        </IntlProvider>
      </ConfigProvider>
    </>
  );
};

export default memo(App);
